import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Users, TrendingUp, LogOut, Plus, Edit, Trash2, Upload, Image as ImageIcon, List, Database, HelpCircle, Cloud, MessageSquare, Tag } from 'lucide-react';
import axios from 'axios';
import { formatPrice } from '../../utils/priceFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_orders: 0,
    total_revenue: 0,
    avg_order_value: 0
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/orders/stats/summary/`, { headers }),
        axios.get(`${API}/admin/products/`, { headers })
      ]);

      setStats(statsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-celadon rounded-lg flex items-center justify-center">
              <div className="text-white font-bold text-xl">O</div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Administration Osyla</h1>
              <p className="text-sm text-gray-600">Gestion du back office</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          >
            <LogOut size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <Package className="text-blue-500" size={32} />
            </div>
            <p className="text-gray-600 text-sm mb-1">Produits</p>
            <p className="text-3xl font-bold text-gray-900">{products.length}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <ShoppingCart className="text-green-500" size={32} />
            </div>
            <p className="text-gray-600 text-sm mb-1">Commandes</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_orders}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-purple-500" size={32} />
            </div>
            <p className="text-gray-600 text-sm mb-1">Chiffre d'affaires</p>
            <p className="text-3xl font-bold text-gray-900">{formatPrice(stats.total_revenue)} €</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="text-orange-500" size={32} />
            </div>
            <p className="text-gray-600 text-sm mb-1">Panier moyen</p>
            <p className="text-3xl font-bold text-gray-900">{formatPrice(stats.avg_order_value)} €</p>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <button
            onClick={() => navigate('/admin/products')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <Package className="text-blue-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Gestion des produits</h3>
            <p className="text-gray-600">Gérer les toiles de store, ajouter, modifier ou supprimer des produits</p>
          </button>

          <button
            onClick={() => navigate('/admin/orders')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <ShoppingCart className="text-green-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Gestion des commandes</h3>
            <p className="text-gray-600">Consulter et gérer les commandes clients, mettre à jour les statuts</p>
          </button>

          <button
            onClick={() => navigate('/admin/quote-requests')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <MessageSquare className="text-purple-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Demandes de devis</h3>
            <p className="text-gray-600">Consulter et suivre les demandes de devis personnalisées</p>
          </button>

          <button
            onClick={() => navigate('/admin/promo-codes')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <Tag className="text-pink-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Codes Promo</h3>
            <p className="text-gray-600">Créer et gérer les codes de réduction clients</p>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-6 mt-6">
          <button
            onClick={() => navigate('/admin/pricing')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <TrendingUp className="text-orange-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Gestion des prix</h3>
            <p className="text-gray-600">Gérer les catégories de prix et les formules de calcul</p>
          </button>

          <button
            onClick={() => navigate('/admin/delivery')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <Package className="text-blue-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Modes de livraison</h3>
            <p className="text-gray-600">Gérer les prix des modes de livraison</p>
          </button>

          <button
            onClick={() => navigate('/admin/import')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <Upload className="text-green-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Import Excel</h3>
            <p className="text-gray-600">Importer des produits depuis un fichier Excel</p>
          </button>

          <button
            onClick={() => navigate('/admin/cloudinary')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left border-2 border-blue-200"
          >
            <Cloud className="text-blue-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">☁️ Images Cloudinary</h3>
            <p className="text-gray-600">Gérer les images cloud partagées entre les sites</p>
          </button>

          <button
            onClick={() => navigate('/admin/collections-order')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <List className="text-indigo-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Ordre des Collections</h3>
            <p className="text-gray-600">Organiser l'ordre d'affichage des collections</p>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-6 mt-6">
          <button
            onClick={() => navigate('/admin/clients')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <Users className="text-purple-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Gestion des clients</h3>
            <p className="text-gray-600">Voir la liste des clients et leur historique de commandes</p>
          </button>
          
          <button
            onClick={() => navigate('/admin/pro-users')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <Users className="text-green-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Comptes PRO 🇫🇷</h3>
            <p className="text-gray-600">Gérer les demandes et comptes professionnels</p>
          </button>
          
          <button
            onClick={() => navigate('/admin/database-check')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <Database className="text-red-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Vérification BDD</h3>
            <p className="text-gray-600">Vérifier la cohérence de la base de données (doublons, données manquantes)</p>
          </button>
          
          <button
            onClick={() => navigate('/admin/tooltips')}
            className="bg-white rounded-lg shadow-sm p-8 hover:shadow-md transition-shadow text-left"
          >
            <HelpCircle className="text-celadon mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Bulles d'aide</h3>
            <p className="text-gray-600">Gérer les textes d'aide affichés sur le site (points d'interrogation)</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;